import { useReducer, useState, type FormEvent } from 'react';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import type { IntakeSpec, CadCategory, IntakeSubType, IntakeDetails, DrawingPreferences, IntakeCore } from '@shared/types';
import { IntakeSpecSchema } from '@/lib/intakeSchema';
import { completeIntakeDraft, defaultDrawingPreferences, defaultIntakeCore } from '@/lib/intakeDefaults';
import { CategorySection } from './sections/CategorySection';
import { SubTypeSection } from './sections/SubTypeSection';
import { QualityLevelSection } from './sections/QualityLevelSection';
import { CoreSection } from './sections/CoreSection';
import { DrawingSection } from './sections/DrawingSection';
import { DetailsSection } from './sections/DetailsSection';
import { FreeformSection } from './sections/FreeformSection';
import { useTranslation } from 'react-i18next';

type State = Partial<IntakeSpec> & { errors?: Record<string, string> };

type Action =
  | { type: 'set-category'; value: CadCategory }
  | { type: 'set-subType'; value: IntakeSubType }
  | { type: 'set-qualityLevel'; value: IntakeSpec['qualityLevel'] }
  | { type: 'set-core'; value: IntakeCore }
  | { type: 'set-drawing'; value: DrawingPreferences }
  | { type: 'set-details'; value: IntakeDetails }
  | { type: 'set-freeform'; value: string }
  | { type: 'errors'; errors: Record<string, string> };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'set-category':
      // Reset subType + details when category changes (mismatch would fail Zod refine).
      return { ...state, category: action.value, subType: undefined, details: undefined, errors: {} };
    case 'set-subType':
      return { ...state, subType: action.value, details: undefined, errors: {} };
    case 'set-qualityLevel':
      return { ...state, qualityLevel: action.value, errors: {} };
    case 'set-core':
      return { ...state, core: action.value, errors: {} };
    case 'set-drawing':
      return { ...state, drawing: action.value, errors: {} };
    case 'set-details':
      return { ...state, details: action.value, errors: {} };
    case 'set-freeform':
      return { ...state, freeform: action.value, errors: {} };
    case 'errors':
      return { ...state, errors: action.errors };
  }
}

export function IntakeForm({
  initial,
  onSubmit,
}: {
  initial?: Partial<IntakeSpec>;
  onSubmit: (intake: IntakeSpec) => void;
}) {
  const { t } = useTranslation('intake');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [state, dispatch] = useReducer(reducer, {
    qualityLevel: 'standard',
    freeform: '',
    ...(initial ?? {}),
  } as State);

  function handleSubmit(e: FormEvent): void {
    e.preventDefault();
    const candidate = {
      ...completeIntakeDraft(state),
      submittedAt: new Date().toISOString(),
    };
    const result = IntakeSpecSchema.safeParse(candidate);
    if (!result.success) {
      const errors: Record<string, string> = {};
      for (const issue of result.error.issues) {
        errors[issue.path.join('.')] = issue.message;
      }
      dispatch({ type: 'errors', errors });
      return;
    }
    onSubmit(result.data);
  }

  const sectionLabel = 'mb-1.5 text-[10px] font-medium uppercase tracking-wider text-synergy-text-tertiary';
  const readyForSimpleInput = Boolean(state.category && state.subType);
  const qualityLevel = state.qualityLevel ?? 'standard';

  function handleIntendedUseChange(intendedUse: string): void {
    if (!state.category || !state.subType) return;
    dispatch({
      type: 'set-core',
      value: {
        ...defaultIntakeCore(state.category, state.subType, intendedUse),
        ...state.core,
        intendedUse,
      },
    });
  }

  return (
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-3">
      <section>
        <h2 className={sectionLabel}>{t('category')}</h2>
        <CategorySection
          value={state.category}
          onChange={(v) => dispatch({ type: 'set-category', value: v })}
        />
      </section>

      {state.category && (
        <section>
          <h2 className={sectionLabel}>{t('subType')}</h2>
          <SubTypeSection
            category={state.category}
            value={state.subType}
            onChange={(v) => dispatch({ type: 'set-subType', value: v })}
          />
        </section>
      )}

      {state.category && state.subType && (
        <>
          <section>
            <h2 className={sectionLabel}>{t('quality')}</h2>
            <QualityLevelSection
              value={qualityLevel}
              onChange={(v) => dispatch({ type: 'set-qualityLevel', value: v })}
            />
          </section>

          <section>
            <h2 className={sectionLabel}>{t('intent')}</h2>
            <input
              type="text"
              value={state.core?.intendedUse ?? ''}
              onChange={(e) => handleIntendedUseChange(e.target.value)}
              maxLength={120}
              placeholder={t('intentPlaceholder')}
              className="w-full rounded-md border border-synergy-neutral-700/40 bg-white/70 px-3 py-2 text-sm shadow-sm outline-none transition-colors focus:border-synergy-blue"
            />
          </section>

          <button
            type="button"
            onClick={() => setAdvancedOpen((open) => !open)}
            className="inline-flex items-center justify-center gap-2 self-center rounded-full border border-synergy-neutral-700/30 bg-white/70 px-4 py-1.5 text-xs font-medium text-synergy-text-secondary transition-colors hover:border-synergy-blue hover:text-synergy-blue"
          >
            <SlidersHorizontal className="h-3.5 w-3.5" />
            <span>{advancedOpen ? t('hideAdvanced') : t('advanced')}</span>
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
          </button>
        </>
      )}

      {readyForSimpleInput && advancedOpen && (
        <>
          <section>
            <h2 className={sectionLabel}>{t('core')}</h2>
            <CoreSection
              value={state.core ?? defaultIntakeCore(state.category!, state.subType!)}
              hideIntendedUse
              onChange={(v) => dispatch({ type: 'set-core', value: v })}
            />
          </section>

          <section>
            <h2 className={sectionLabel}>{t('details')}</h2>
            <DetailsSection
              category={state.category!}
              subType={state.subType!}
              value={state.details}
              onChange={(v) => dispatch({ type: 'set-details', value: v })}
            />
          </section>

          <section>
            <h2 className={sectionLabel}>{t('drawing')}</h2>
            <DrawingSection
              value={state.drawing ?? defaultDrawingPreferences(qualityLevel, state.category)}
              onChange={(v) => dispatch({ type: 'set-drawing', value: v })}
            />
          </section>

          <section>
            <h2 className={sectionLabel}>{t('freeform')}</h2>
            <FreeformSection
              value={state.freeform ?? ''}
              onChange={(v) => dispatch({ type: 'set-freeform', value: v })}
            />
          </section>
        </>
      )}

      {state.errors && Object.keys(state.errors).length > 0 && (
        <div className="rounded-lg border-2 border-brand-indigo bg-brand-cream p-3 text-xs font-semibold text-brand-indigo">
          <div className="mb-1 font-bold">{t('inputError')}</div>
          {Object.entries(state.errors as Record<string, string>).map(([k, v]) => (
            <div key={k}>
              {k}: {v}
            </div>
          ))}
        </div>
      )}

      <button
        type="submit"
        disabled={!state.category || !state.subType}
        className="mt-1 self-center rounded-full bg-synergy-blue px-6 py-2 text-sm font-medium text-white shadow-sm transition-all hover:bg-synergy-blue/90 hover:shadow-md disabled:opacity-50"
      >
        {t('generate')}
      </button>
    </form>
  );
}
